import { defineStore } from "pinia"

import { getParamFromUrl } from "@/services/urls.js"
import * as sentryReporter from "@/services/sentryReporter.js"

import * as messagesApi from "@/api/messagesApi.js"
import * as accountsApi from "@/api/accountsApi.js"

import { useUserSessionStore } from "@/store/userSession.js"

const getChatTitle = (chat) => {
  const userSessionStore = useUserSessionStore()
  return chat.memberships
    .filter((m) => m.user.id !== userSessionStore.user.id)
    .map((m) => m.user.display_name)
    .join(", ")
}

export const useMessagingStore = defineStore("messaging", {
  state: () => ({
    chatsState: [],
    selectedChatState: null,
    chatsLoadingState: false,
    chatsNextCursorState: null,
    chatMessagesLoadingState: false,
    selectedNewChatContactsState: [],
    chatWithNewFriendLoadingState: false,
    savedMessagesToSendState: []
  }),
  getters: {
    chats: (state) => state.chatsState,
    chatsLoading: (state) => state.chatsLoadingState,
    selectedChat: (state) => state.selectedChatState,
    selectedChatOtherParticipants: (state) => {
      const userSessionStore = useUserSessionStore()
      // Theoretically userSession/user should not be null, but based on our logs sometimes it is
      // https://disciple.sentry.io/issues/3964694384/events/edb9c3f829d64cfdb4f0872ddd1bba55/?project=6718259&project=4504451893624832
      return state.selectedChatState?.memberships?.filter((m) => m.user.id !== userSessionStore.user?.id).map((m) => m.user)
    },
    chatsNextCursor: (state) => state.chatsNextCursorState,
    chatMessagesLoading: (state) => state.chatMessagesLoadingState,
    selectedNewChatContacts: (state) => state.selectedNewChatContactsState,
    chatWithNewFriendLoading: (state) => state.chatWithNewFriendLoadingState,
    getSavedMessage: (state) => (conversationId) => state.savedMessagesToSendState.find((s) => s.conversationId === conversationId)?.message
  },
  actions: {
    async fetchChats() {
      if (this.chatsLoadingState) {
        return
      }

      this.chatsLoadingState = true
      try {
        const { conversations: newChats, meta } = await messagesApi.chats(this.chatsNextCursorState)

        const validChats = newChats.filter((c) => !this.chatsState.find((chat) => chat.id === c.id)).map((chat) => ({ ...chat, title: getChatTitle(chat) }))

        this.chatsNextCursorState = getParamFromUrl(meta.next, "older_than")
        this.chatsState = [...this.chatsState, ...validChats]
      } catch (err) {
        sentryReporter.reportError(err)
      } finally {
        this.chatsLoadingState = false
      }
    },
    clearChats() {
      this.chatsState = []
      this.selectedChatState = null
      this.chatsNextCursorState = null
      this.chatsLoadingState = false
      this.chatMessagesLoadingState = false
    },
    clearSavedMessages() {
      this.savedMessagesToSendState = []
    },
    async selectChat(chat) {
      if (this.chatMessagesLoadingState && this.selectedChatState) {
        return
      }

      const newChatInTheList = this.chatsState.find((c) => c.new)
      if (chat.new && newChatInTheList) {
        this.selectedChatState = newChatInTheList
        return
      }

      const alreadyFetchedChat = this.chatsState.find((c) => Number(c.id) === Number(chat.id))

      if (alreadyFetchedChat) {
        Object.assign(alreadyFetchedChat, chat)
        this.selectedChatState = alreadyFetchedChat
      } else {
        try {
          this.chatMessagesLoadingState = true
          const { conversation: notYetFetchedChat } = await messagesApi.chat(chat.id)

          notYetFetchedChat.title = getChatTitle(notYetFetchedChat)

          this.chatsState.unshift(notYetFetchedChat)
          this.selectedChatState = notYetFetchedChat
        } catch (err) {
          sentryReporter.reportError(err)
        } finally {
          this.chatMessagesLoadingState = false
        }
      }
    },
    deselectChat() {
      this.selectedChatState = null
    },
    async requestChatWithContacts({ contactsIds, withNewFriend, newChatWithExistingFriend }) {
      this.chatMessagesLoadingState = true
      if (withNewFriend) {
        this.chatWithNewFriendLoadingState = true
      }

      const userSessionStore = useUserSessionStore()
      const { conversations } = await messagesApi.chatWithMembers({ membersIds: [userSessionStore.user.id, ...contactsIds] })

      const existingConversation = conversations[0]

      if (existingConversation) {
        if (!this.chatsState.find((c) => c.id === existingConversation.id)) {
          existingConversation.title = getChatTitle(existingConversation)
          this.chatsState.unshift(existingConversation)
        }
        this.selectChat(existingConversation)
      } else {
        let contacts = []

        if (withNewFriend) {
          const { user } = await accountsApi.getUser(contactsIds[0])
          contacts = [{ user }]
        } else {
          contacts = this.selectedNewChatContactsState.map((contact) => ({ user: contact }))
        }

        this.addTemporaryNewChat({ contacts, withNewFriend, newChatWithExistingFriend })
        this.selectedNewChatContactsState = []
      }

      this.chatMessagesLoadingState = false
      if (withNewFriend) {
        this.chatWithNewFriendLoadingState = false
      }
    },
    addTemporaryNewChat({ contacts, withNewFriend, newChatWithExistingFriend }) {
      const userSessionStore = useUserSessionStore()
      const temporaryNewChat = {
        new: true,
        memberships: [{ user: userSessionStore.user }, ...contacts],
        withNewFriendId: withNewFriend ? contacts[0].user.id : null,
        newChatWithExistingFriend
      }

      temporaryNewChat.title = getChatTitle(temporaryNewChat)

      this.chatsState = [temporaryNewChat, ...this.chatsState.filter((chat) => !chat.new)]
      this.selectedChatState = temporaryNewChat
    },
    updateChat({ chatIndex, chat }) {
      let replacedTempChat = false
      if (chatIndex > -1) {
        this.chatsState.splice(chatIndex, 1)
      } else if (chat.memberships.length === 2) {
        //This handles situation when there is temporary new chat created after accepting new request from user X
        //Then while user has messaging opened, user X sends message which creates new conversation and unshifts it to the chats
        //So now we should remove the temporary chat, we can find it by matching users ids
        const indexOfTempChatWithTheSameUser = this.chatsState.findIndex((c) => chat.memberships.map((m) => m.user.id).includes(c.withNewFriendId))

        if (indexOfTempChatWithTheSameUser > -1) {
          this.chatsState.splice(indexOfTempChatWithTheSameUser, 1)
          replacedTempChat = true
        }
      }

      this.chatsState.unshift(chat)
      if (replacedTempChat) {
        this.selectedChatState = chat
      }
    },
    async makeTemporaryNewChatPermanent({ chatId }) {
      const temporaryNewChatIndex = this.chatsState.findIndex((c) => c.new)
      if (temporaryNewChatIndex < 0) {
        return
      }

      const { conversation: chat } = await messagesApi.chat(chatId)
      chat.title = this.chatsState[temporaryNewChatIndex].title

      this.updateChat({ chatIndex: temporaryNewChatIndex, chat })
      this.selectedChatState = chat
    },
    async updateChatLastMessage({ chatId, message }) {
      const chatIndex = this.chatsState.findIndex((c) => Number(c.id) === Number(chatId))

      let chat

      if (chatIndex > -1) {
        chat = { ...this.chatsState[chatIndex], last_message: message, last_action_at: message.time }
      } else {
        try {
          const { conversation } = await messagesApi.chat(chatId)
          chat = conversation
          chat.title = getChatTitle(chat)
        } catch (err) {
          sentryReporter.reportError(err)
        }
      }
      this.updateChat({ chatIndex, chat })
    },
    setChatMessagesLoading(chatMessagesLoading) {
      this.chatMessagesLoadingState = chatMessagesLoading
    },
    setSelectedNewChatContacts(selectedNewChatContacts) {
      this.selectedNewChatContactsState = selectedNewChatContacts
    },
    addSavedMessage({ message, conversationId }) {
      const existing = this.savedMessagesToSendState.find((s) => s.conversationId === conversationId)

      if (existing) {
        existing.message = message
      } else {
        this.savedMessagesToSendState.push({ message, conversationId })
      }
    },
    removeSavedMessage(conversationId) {
      const index = this.savedMessagesToSendState.findIndex((s) => s.conversationId === conversationId)
      if (index > -1) {
        this.savedMessagesToSendState.splice(index, 1)
      }
    },
    async leaveSelectedChat() {
      if (!this.selectedChatState.new) {
        await messagesApi.leaveConversation(this.selectedChatState.id)
      }

      const index = this.chatsState.findIndex((c) => c.id === this.selectedChatState.id)
      if (index > -1) {
        this.chatsState.splice(index, 1)
      }
      this.deselectChat()
    }
  }
})
